import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { SearchDialog } from "./SearchDialog";
import { cn } from "@/lib/utils";

interface NavbarProps {
  children?: React.ReactNode;
}

const navLinks = [
  { to: "/", label: "home" },
  { to: "/dashboard", label: "dashboard" },
  { to: "/leaderboard", label: "leaderboard" },
  { to: "/playground", label: "playground" },
  { to: "/blog", label: "blog" },
  { to: "/profile", label: "profile" },
];

function useUptime() {
  const [t, setT] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setT(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // anchor uptime to a fixed boot date so it always shows a believable number
  const BOOT = new Date("2009-12-24T00:00:00Z").getTime();
  const ms = Math.max(0, t - BOOT);
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return { d, h, m, s };
}

export function Navbar({ children }: NavbarProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { d, h, m, s } = useUptime();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [menuOpen]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
    navigate("/");
  };

  const sessionId = user?.id ? user.id.slice(0, 6).toUpperCase() : "GUEST";

  // Filter profile out for guests
  const visibleLinks = user
    ? navLinks
    : navLinks.filter((l) => l.to !== "/profile" && l.to !== "/dashboard");

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border">
      {/* Top status strip */}
      <div className="border-b border-border/60 px-3 md:px-5 py-1.5 flex flex-col md:flex-row md:items-center md:justify-between gap-1 text-[10px] uppercase tracking-[0.2em]">
        <div className="flex items-center gap-4 md:gap-6 min-w-0">
          <span className="text-primary font-bold shrink-0">FRISYRE_OS v9.4.1</span>
          <span className="text-muted-foreground hidden sm:inline truncate">
            session: <span className="text-foreground/70 tabular-nums">{sessionId}</span>
          </span>
          <span className="text-muted-foreground hidden md:inline">
            tty: <span className="text-foreground/70">/dev/pts/0</span>
          </span>
        </div>
        <div className="flex items-center gap-4 md:gap-6 text-muted-foreground tabular-nums">
          <span className="hidden sm:inline">
            up: <span className="text-foreground/70">{d}d {String(h).padStart(2, "0")}h {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s</span>
          </span>
          <span className="hidden md:inline">
            load: <span className="text-foreground/70">0.14 0.08 0.05</span>
          </span>
          <span className="text-primary tui-status">online</span>
        </div>
      </div>

      {/* Main nav row */}
      <nav className="px-3 md:px-5 py-2.5 flex items-center justify-between gap-2 md:gap-3">
        <div className="flex items-center gap-4 md:gap-6 min-w-0">
          <Link
            to="/"
            className="flex items-center shrink-0 group"
            aria-label="FRISYRE home"
          >
            <span className="text-sm font-bold tracking-[0.25em] uppercase text-foreground group-hover:text-primary transition-colors">
              <span className="text-primary">/</span>FRISYRE
            </span>
          </Link>

          <ul className="hidden md:flex items-center gap-1 text-xs">
            {visibleLinks.map((link) => {
              const active = location.pathname === link.to;
              return (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={cn(
                      "px-2.5 py-1 uppercase tracking-[0.15em] transition-colors",
                      active
                        ? "text-primary border-b border-primary"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    ./{link.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <SearchDialog />
          {children}
          {user ? (
            <button
              onClick={handleSignOut}
              className="hidden sm:inline-flex h-7 px-3 items-center text-[11px] font-mono uppercase tracking-[0.15em] border border-border text-muted-foreground hover:text-destructive hover:border-destructive transition-colors"
            >
              [exit]
            </button>
          ) : (
            <Link
              to="/auth"
              className="hidden sm:inline-flex h-7 px-3 items-center text-[11px] font-mono uppercase tracking-[0.15em] bg-primary text-primary-foreground border border-primary hover:bg-primary/90 font-bold"
            >
              [login]
            </Link>
          )}

          {/* Mobile hamburger — visible below md */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-drawer"
            className="md:hidden h-8 w-9 inline-flex flex-col items-center justify-center gap-[3px] border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors"
          >
            <span
              className={cn(
                "block h-[1.5px] w-4 bg-current transition-transform",
                menuOpen && "translate-y-[5px] rotate-45",
              )}
            />
            <span
              className={cn(
                "block h-[1.5px] w-4 bg-current transition-opacity",
                menuOpen && "opacity-0",
              )}
            />
            <span
              className={cn(
                "block h-[1.5px] w-4 bg-current transition-transform",
                menuOpen && "-translate-y-[5px] -rotate-45",
              )}
            />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          className="md:hidden fixed inset-0 top-0 z-40 bg-background pt-[88px] overflow-y-auto"
          onClick={() => setMenuOpen(false)}
        >
          <div
            id="mobile-nav-drawer"
            className="border-t border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-b border-border/60">
              ┌─[ ./nav ]
            </div>
            <ul className="flex flex-col">
              {visibleLinks.map((link) => {
                const active = location.pathname === link.to;
                return (
                  <li key={link.to} className="border-b border-border/40">
                    <Link
                      to={link.to}
                      onClick={() => setMenuOpen(false)}
                      className={cn(
                        "flex items-center justify-between px-4 py-3.5 text-[13px] uppercase tracking-[0.18em] font-mono transition-colors",
                        active
                          ? "text-primary bg-primary/5"
                          : "text-foreground/80 hover:text-primary hover:bg-primary/5",
                      )}
                    >
                      <span>./{link.label}</span>
                      <span className="text-muted-foreground">↵</span>
                    </Link>
                  </li>
                );
              })}
            </ul>

            <div className="px-3 pt-4 pb-6 flex flex-col gap-2 border-t border-border/60 bg-muted/10">
              {user ? (
                <button
                  onClick={handleSignOut}
                  className="w-full h-10 text-[12px] font-mono uppercase tracking-[0.18em] border border-border text-destructive hover:bg-destructive/10 hover:border-destructive transition-colors"
                >
                  [ exit session ]
                </button>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setMenuOpen(false)}
                  className="w-full h-10 inline-flex items-center justify-center text-[12px] font-mono uppercase tracking-[0.18em] bg-primary text-primary-foreground border border-primary font-bold"
                >
                  [ login / register ]
                </Link>
              )}
              <div className="flex justify-between text-[9px] uppercase tracking-[0.22em] text-muted-foreground pt-2">
                <span>session: {sessionId}</span>
                <span>up: {d}d {String(h).padStart(2, "0")}h {String(m).padStart(2, "0")}m</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                └─[ exit 0 ]
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
